"use client";
import { useState, useEffect } from "react";
import { Select } from "@repo/ui/Select";
import { InputFieldProps } from "@repo/ui/InputField";
import { SelectOption } from "./Select";
import { InputField } from "./InputField";

export function PhoneNumberSelect({
  field,
  options,
  value = "",
  onChange,
  onBlur,
}: {
  field: InputFieldProps;
  options: SelectOption[];
  value: string;
  onChange: (countryCode: string, phoneNumber: string) => void;
  onBlur?: () => void;
}) {
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    if (options.length === 0 || selectedCountryCode) return;

    const defaultCountryCode = field.defaultCountryCode;
    const defaultMatch = defaultCountryCode
      ? (options.find((option) => option.value === defaultCountryCode) ??
        options.find((option) => option.label === defaultCountryCode))
      : undefined;

    setSelectedCountryCode(defaultMatch?.value ?? options[0]?.value ?? "");
  }, [options, selectedCountryCode, field.defaultCountryCode]);

  useEffect(() => {
    if (value && options.length > 0) {
      const matchingOption = options.find((option) =>
        value.startsWith(option.value),
      );

      if (matchingOption) {
        setSelectedCountryCode(matchingOption.value);
        setPhoneNumber(value.substring(matchingOption.value.length));
      } else {
        setSelectedCountryCode(options[0]?.value || "");
        setPhoneNumber(value);
      }
    } else if (!value) {
      setPhoneNumber("");
    }
  }, [value, options]);

  const handleCountryChange = (countryCode: string | string[]) => {
    setSelectedCountryCode(countryCode as string);
    onChange(countryCode as string, phoneNumber);
  };

  const handlePhoneChange = (number: string) => {
    setPhoneNumber(number);
    onChange(selectedCountryCode, number);
  };

  const handlePhoneBlur = () => {
    onBlur?.();
  };

  const renderCountryOption = (option: SelectOption) => {
    const parts = option.label.split(" || ");

    if (parts.length === 2) {
      const countryName = parts[0];
      const code = parts[1];

      return (
        <div className="ui:flex ui:w-full ui:items-center ui:justify-between">
          <span className="ui:truncate">{countryName}</span>
          <span className="ui:ml-2 ui:shrink-0 ui:text-textSecondary">
            {code}
          </span>
        </div>
      );
    }

    return <span className="ui:truncate">{option.label}</span>;
  };

  const getDisplayLabel = (optionLabel: string) => {
    const parts = optionLabel.split(" || ");
    return parts.length === 2 ? `${parts[0]} ${parts[1]}` : optionLabel;
  };

  return (
    <div className="ui:flex ui:flex-col ui:gap-2">
      <div className="ui:flex ui:w-full ui:flex-col ui:gap-small ui:md2:flex-row">
        <div className="md:ui:w-fit md:ui:min-w-[184px] ui:w-full">
          <Select
            label={field.label}
            options={options.map((option) => ({
              ...option,
              displayLabel: getDisplayLabel(option.label),
            }))}
            dir="ltr"
            mode="bright"
            value={selectedCountryCode}
            onValueChange={handleCountryChange}
            maxHeight={340}
            optionsGap={"ui:gap-extraSmall"}
            renderOption={renderCountryOption}
          />
        </div>

        <div className="md:ui:flex-1 ui:w-full">
          <InputField
            mode="bright"
            type="tel"
            name={field.name}
            label=""
            ariaLabel={field.label}
            value={phoneNumber}
            state={field.state}
            errorMessage={field.errorMessage}
            onChange={handlePhoneChange}
            onBlur={handlePhoneBlur}
          />
        </div>
      </div>
    </div>
  );
}
